import { useState, useEffect } from 'react';
import { ChevronsUpDown, Loader2, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useFilterStore } from '@/stores/filter-store';
import { useShipmentsStore } from '@/stores/shipments-store';
import { useOperationFilterStore } from '@/stores/operation-filter-store';
import { useLanguage } from '@/contexts/LanguageContext';

interface Shipment {
  id: number;
  order_id: number;
  currency_id: number;
  marketplace_id: number;
  marketplace_product_id: number;
  reference_item_id: string;
  shipment_id: number | null;
  order_product_waybill_id: number;
  order_product_completion_id: number | null;
  code: string;
  first_identifier: string;
  second_identifier: string;
  sku: string;
  mkt_sku: string | null;
  weight: string;
  ai_weight: string | null;
  mkt_weight: string | null;
  customer_selected_category: string | null;
  price: string;
  qty: number;
  is_cancelled: number;
  refund_date: string | null;
  refund_amount: string | null;
  refund_method: string | null;
  refund_giftcard_id: string | null;
  refund_reason: string | null;
  refund_note: string | null;
  subtotal: string;
  shipping: string;
  mkt_shipping_fee: string | null;
  tax: string;
  mahalkum_fee: string;
  discount_price: number;
  total: string;
  send_to_trakpylot: number;
  product_image: string;
  created_at: string;
  updated_at: string;
  mkt_fee_applied: string | null;
  mkt_tax_included: string | null;
  // Legacy shipment fields
  shid: number;
  shipment_reference: string;
  master_way_bill: string;
  mkt_product_name: string | null;
  mkt_order_number: string | null;
  mkt_name: string | null;
  mkt_url: string;
  orig_tracking_no: string | null;
  order_confirmed: string | null;
}

function CustomCheckbox() {
  return (
    <div className="relative">
      <input 
        type="checkbox" 
        className="peer w-4 h-4 rounded border-2 border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2 bg-white appearance-none checked:bg-blue-600 checked:border-blue-600" 
      />
      <svg 
        className="absolute inset-0 w-4 h-4 pointer-events-none opacity-0 peer-checked:opacity-100 text-white" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    </div>
  );
}

// Commercial Invoice Side Drawer Component
function CommercialInvoiceDrawer({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [masterWayBills, setMasterWayBills] = useState('');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer - Matching Notification/Help Desk style */}
      <div 
        className={`fixed top-0 bg-white shadow-2xl transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          width: '656px',
          height: '960px',
          right: '0px',
          top: '0px'
        }}
      >
        {/* Header - Dark blue background */}
        <div className="bg-secondary-500 text-white p-4 flex justify-between items-center">
          <h2 className="text-lg font-semibold">Commercial Invoice</h2>
          <button
            onClick={onClose}
            className="bg-[#426198] px-2 py-2 rounded-full text-white hover:text-gray-300 transition-colors"
          >
            <X size={26} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto" style={{ height: 'calc(960px - 120px)' }}>
          <div className="p-6">
            <h3 className="text-sm text-gray-600 mb-4">Generate Commercial Invoice</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Master WayBills
              </label>
              <input
                type="text"
                value={masterWayBills}
                onChange={(e) => setMasterWayBills(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter master waybills..."
              />
            </div>
            <button className="w-full bg-yellow-500 text-white py-2 px-4 rounded-md hover:bg-yellow-600 transition-colors">
              Generate Invoice
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Adjust Side Drawer Component
function AdjustDrawer({ isOpen, onClose, shipment: _shipment }: { isOpen: boolean; onClose: () => void; shipment: Shipment | null }) {
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Mock data matching the Figma design
  const mockShipmentOrders = [
    {
      id: 1,
      itemOrderId: '01-0823-42199',
      masterWaybill: 'IQ2508231',
      marketplaceProductName: '11-367 Kadın Ortopedic terlik Fiyatları',
      marketplaceOrderNo: 'Not Set',
      marketplaceName: 'Flo',
      marketplaceUrl: 'https://diamindring/...'
    },
    {
      id: 2,
      itemOrderId: '01-0823-42200',
      masterWaybill: '21 Oct, 2024',
      marketplaceProductName: '11-367 Kadın Ortopedic terlik Fiyatları',
      marketplaceOrderNo: 'Not Set',
      marketplaceName: 'Trendyol',
      marketplaceUrl: 'https://diamindring/...'
    },
    {
      id: 3,
      itemOrderId: '01-0823-42201',
      masterWaybill: 'IQ2508232',
      marketplaceProductName: '11-367 Kadın Ortopedic terlik Fiyatları',
      marketplaceOrderNo: 'Not Set',
      marketplaceName: 'Flo',
      marketplaceUrl: 'https://diamindring/...'
    },
    {
      id: 4,
      itemOrderId: '01-0823-42202',
      masterWaybill: '22 Oct, 2024',
      marketplaceProductName: '11-367 Kadın Ortopedic terlik Fiyatları',
      marketplaceOrderNo: 'Not Set',
      marketplaceName: 'Trendyol',
      marketplaceUrl: 'https://diamindring/...'
    },
    {
      id: 5,
      itemOrderId: '01-0823-42203',
      masterWaybill: 'IQ2508233',
      marketplaceProductName: '11-367 Kadın Ortopedic terlik Fiyatları',
      marketplaceOrderNo: 'Not Set',
      marketplaceName: 'Flo',
      marketplaceUrl: 'https://diamindring/...'
    }
  ];

  const totalItems = 52; // Total count from Figma
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handleSelectAll = () => {
    if (selectedItems.size === mockShipmentOrders.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(mockShipmentOrders.map(item => item.id)));
    }
  };

  const handleSelectItem = (id: number) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  const handleDeleteSelected = () => {
    // Handle delete selected items
    console.log('Deleting selected items:', Array.from(selectedItems));
  };

  const handleAdjustShipment = () => {
    // Handle adjust shipment
    console.log('Adjusting shipment');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer - Matching Notification/Help Desk style */}
      <div 
        className={`fixed top-0 bg-white shadow-2xl transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          width: '1200px',
          height: '960px',
          right: '0px',
          top: '0px'
        }}
      >
        {/* Header - Dark blue background */}
        <div className="bg-secondary-500 text-white p-4 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold">Adjust</h2>
            <p className="text-sm text-gray-300 mt-1">Select secondary currency</p>
          </div>
          <button
            onClick={onClose}
            className="bg-[#426198] px-2 py-2 rounded-full text-white hover:text-gray-300 transition-colors"
          >
            <X size={26} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto" style={{ height: 'calc(960px - 120px)' }}>
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Shipment Orders</h3>
              <span className="text-sm text-gray-600">Total Count: 5</span>
            </div>
            
            {/* Table */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1000px]">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="py-3 px-4 text-left">
                        <input 
                          type="checkbox" 
                          checked={selectedItems.size === mockShipmentOrders.length}
                          onChange={handleSelectAll}
                          className="w-4 h-4 rounded border-2 border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2 bg-white appearance-none checked:bg-blue-600 checked:border-blue-600"
                        />
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-medium text-gray-700">
                        <div className="flex items-center gap-1">
                          Item Order ID
                          <ChevronsUpDown size={14} className="text-gray-400" />
                        </div>
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-medium text-gray-700">
                        <div className="flex items-center gap-1">
                          Master waybill
                          <ChevronsUpDown size={14} className="text-gray-400" />
                        </div>
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-medium text-gray-700">
                        <div className="flex items-center gap-1">
                          Marketplace Product Name
                          <ChevronsUpDown size={14} className="text-gray-400" />
                        </div>
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-medium text-gray-700">
                        <div className="flex items-center gap-1">
                          Marketplace Order No
                          <ChevronsUpDown size={14} className="text-gray-400" />
                        </div>
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-medium text-gray-700">
                        <div className="flex items-center gap-1">
                          Marketplace Name
                          <ChevronsUpDown size={14} className="text-gray-400" />
                        </div>
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-medium text-gray-700">
                        <div className="flex items-center gap-1">
                          Marketplace URL
                          <ChevronsUpDown size={14} className="text-gray-400" />
                        </div>
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-medium text-gray-700">
                        <div className="flex items-center gap-1">
                          Action
                          <ChevronsUpDown size={14} className="text-gray-400" />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {mockShipmentOrders.map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <input 
                            type="checkbox" 
                            checked={selectedItems.has(order.id)}
                            onChange={() => handleSelectItem(order.id)}
                            className="w-4 h-4 rounded border-2 border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2 bg-white appearance-none checked:bg-blue-600 checked:border-blue-600"
                          />
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900">
                          {order.itemOrderId}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900">
                          {order.masterWaybill}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900">
                          {order.marketplaceProductName}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900">
                          {order.marketplaceOrderNo}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900">
                          {order.marketplaceName}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900">
                          {order.marketplaceUrl}
                        </td>
                        <td className="py-3 px-4">
                          <button className="bg-yellow-500 hover:bg-yellow-600 p-1 rounded">
                            <svg className="w-4 h-4" fill="black" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Show</span>
                <select 
                  value={itemsPerPage} 
                  className="border border-gray-300 rounded px-2 py-1 text-sm text-gray-700"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                </select>
                <span className="text-sm text-gray-700">per page</span>
              </div>
              
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-700">
                  1-10 of 52
                </span>
                
                <div className="flex items-center gap-1">
                  <button 
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4 text-gray-600" />
                  </button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 text-sm rounded ${
                          currentPage === pageNum 
                            ? 'bg-[#F1F1F4] text-gray-900 font-medium' 
                            : 'text-gray-500 hover:bg-gray-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button 
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-4 justify-end">
              <button 
                onClick={handleDeleteSelected}
                className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700 bg-white"
              >
                Delete Selected
              </button>
              <button 
                onClick={handleAdjustShipment}
                className="px-6 py-2 bg-yellow-500 text-black rounded-md hover:bg-yellow-600 font-medium"
              >
                Adjust Shipment
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Entry Side Drawer Component
function EntryDrawer({ isOpen, onClose, shipment: _shipment }: { isOpen: boolean; onClose: () => void; shipment: Shipment | null }) {
  const [formData, setFormData] = useState({
    expectedDelivery: '',
    carrierName: '',
    carrierPhoneNumber: '',
    carrierWayBill: '',
    carrierWayBillUrl: '',
    noOfPackages: '4',
    multipleShipmentNumber: '4',
    mwShippingCost: '',
    mwShippingCostCurrency: 'Not Set',
    mwCustomCost: '',
    mwCustomCostCurrency: 'Not Set',
    totalWeight: '',
    estimatedShippingCost: '',
    vatClaimingCost: '',
    estimatedDomesticShippingCost: ''
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer - Matching Notification/Help Desk style */}
      <div 
        className={`fixed top-0 bg-white shadow-2xl transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          width: '656px',
          height: '960px',
          right: '0px',
          top: '0px'
        }}
      >
        {/* Header - Dark blue background */}
        <div className="bg-secondary-500 text-white p-4 flex justify-between items-center">
          <h2 className="text-lg font-semibold">Entry</h2>
          <button
            onClick={onClose}
            className="bg-[#426198] px-2 py-2 rounded-full text-white hover:text-gray-300 transition-colors"
          >
            <X size={26} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto" style={{ height: 'calc(960px - 120px)' }}>
          <div className="p-6">
                         <div className="grid grid-cols-2 gap-4">
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Expected Delivery</label>
                 <input
                   type="text"
                   value={formData.expectedDelivery}
                   onChange={(e) => setFormData({...formData, expectedDelivery: e.target.value})}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                 />
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Carrier Name</label>
                 <input
                   type="text"
                   value={formData.carrierName}
                   onChange={(e) => setFormData({...formData, carrierName: e.target.value})}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                 />
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Carrier Phone Number</label>
                 <input
                   type="text"
                   value={formData.carrierPhoneNumber}
                   onChange={(e) => setFormData({...formData, carrierPhoneNumber: e.target.value})}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                 />
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Carrier WayBill</label>
                 <input
                   type="text"
                   value={formData.carrierWayBill}
                   onChange={(e) => setFormData({...formData, carrierWayBill: e.target.value})}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                 />
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Carrier WayBill URL</label>
                 <input
                   type="text"
                   value={formData.carrierWayBillUrl}
                   onChange={(e) => setFormData({...formData, carrierWayBillUrl: e.target.value})}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                 />
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">No. of Packages</label>
                 <input
                   type="text"
                   value={formData.noOfPackages}
                   onChange={(e) => setFormData({...formData, noOfPackages: e.target.value})}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                 />
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Multiple Shipment Number</label>
                 <input
                   type="text"
                   value={formData.multipleShipmentNumber}
                   onChange={(e) => setFormData({...formData, multipleShipmentNumber: e.target.value})}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                 />
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">MW Shipping Cost</label>
                 <input
                   type="text"
                   value={formData.mwShippingCost}
                   onChange={(e) => setFormData({...formData, mwShippingCost: e.target.value})}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                 />
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">MW Shipping Cost Currency</label>
                 <select
                   value={formData.mwShippingCostCurrency}
                   onChange={(e) => setFormData({...formData, mwShippingCostCurrency: e.target.value})}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                 >
                   <option value="Not Set">Not Set</option>
                   <option value="USD">USD</option>
                   <option value="EUR">EUR</option>
                   <option value="GBP">GBP</option>
                 </select>
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">MW Custom Cost</label>
                 <input
                   type="text"
                   value={formData.mwCustomCost}
                   onChange={(e) => setFormData({...formData, mwCustomCost: e.target.value})}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                 />
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">MW Custom Cost Currency</label>
                 <select
                   value={formData.mwCustomCostCurrency}
                   onChange={(e) => setFormData({...formData, mwCustomCostCurrency: e.target.value})}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                 >
                   <option value="Not Set">Not Set</option>
                   <option value="USD">USD</option>
                   <option value="EUR">EUR</option>
                   <option value="GBP">GBP</option>
                 </select>
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Total Weight</label>
                 <input
                   type="text"
                   value={formData.totalWeight}
                   onChange={(e) => setFormData({...formData, totalWeight: e.target.value})}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                 />
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Shipping Cost</label>
                 <input
                   type="text"
                   value={formData.estimatedShippingCost}
                   onChange={(e) => setFormData({...formData, estimatedShippingCost: e.target.value})}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                 />
               </div>
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">VAT Claiming Cost</label>
                 <input
                   type="text"
                   value={formData.vatClaimingCost}
                   onChange={(e) => setFormData({...formData, vatClaimingCost: e.target.value})}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                 />
               </div>
             </div>
             
             {/* Estimated Domestic Shipping Cost - Full Width */}
             <div className="mt-4">
               <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Domestic Shipping Cost</label>
               <input
                 type="text"
                 value={formData.estimatedDomesticShippingCost}
                 onChange={(e) => setFormData({...formData, estimatedDomesticShippingCost: e.target.value})}
                 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
               />
             </div>
             
             {/* Generate Finance Report CSV Button - Full Width */}
             <div className="mt-4">
               <button className="w-full px-6 py-2 bg-[#142D57] text-white rounded-md hover:bg-[#1a3a6b] transition-colors">
                 Generate Finance Report CSV
               </button>
             </div>
             
             {/* Cancel and Save Buttons */}
             <div className="flex gap-4 justify-end mt-6">
               <button 
                 onClick={onClose} 
                 className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700 bg-white"
               >
                 Cancel
               </button>
               <button className="px-6 py-2 bg-yellow-500 text-black rounded-md hover:bg-yellow-600 font-medium">
                 Save
               </button>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Secondary Currency Side Drawer Component
function SecondaryCurrencyDrawer({ isOpen, onClose, shipment: _shipment }: { isOpen: boolean; onClose: () => void; shipment: Shipment | null }) {
  const [selectedCurrency, setSelectedCurrency] = useState('USD');

  const currencies = [
    { code: 'USD', flag: '/img/currency/usd.svg', name: 'United States' },
    { code: 'TRY', flag: '/img/currency/try.svg', name: 'Turkey' },
    { code: 'IQD', flag: '/img/currency/iqd.svg', name: 'Iraq' },
    { code: 'SAR', flag: '/img/currency/sar.svg', name: 'Saudi Arabia' },
    { code: 'AED', flag: '/img/currency/aed.svg', name: 'United Arab Emirates' },
    { code: 'EUR', flag: '/img/currency/eur.svg', name: 'European Union' },
    { code: 'GBP', flag: '/img/currency/gbp.svg', name: 'United Kingdom' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer - Matching Notification/Help Desk style */}
      <div 
        className={`fixed top-0 bg-white shadow-2xl transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          width: '656px',
          height: '960px',
          right: '0px',
          top: '0px'
        }}
      >
        {/* Header - Dark blue background */}
        <div className="bg-secondary-500 text-white p-4 flex justify-between items-center">
          <h2 className="text-lg font-semibold">Secondary Currency</h2>
          <button
            onClick={onClose}
            className="bg-[#426198] px-2 py-2 rounded-full text-white hover:text-gray-300 transition-colors"
          >
            <X size={26} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto" style={{ height: 'calc(960px - 120px)' }}>
          <div className="p-6">
            {/* Subtitle */}
            <p className="text-sm text-gray-600 mb-4">Select secondary currency</p>
            
            <div className="space-y-2">
              {currencies.map((currency) => (
                <button
                  key={currency.code}
                  onClick={() => setSelectedCurrency(currency.code)}
                  className={`w-full p-3 rounded-lg border text-left flex items-center gap-3 transition-colors ${
                    selectedCurrency === currency.code
                      ? 'bg-[#FFF8DD] text-gray-900 border-[#FFF8DD]'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <img 
                    src={currency.flag} 
                    alt={`${currency.name} flag`} 
                    className="w-6 h-4 object-cover rounded"
                  />
                  <span className="font-medium">{currency.code}</span>
                </button>
              ))}
            </div>
            
                         <div className="flex gap-4 justify-end mt-6">
               <button 
                 onClick={onClose} 
                 className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700 bg-white"
               >
                 Cancel
               </button>
               <button className="px-6 py-2 bg-yellow-500 text-black rounded-md hover:bg-yellow-600 font-medium">
                 Save
               </button>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ShipmentsTable({ 
  storeId,
//   searchQuery,
//   filters
}: { 
  storeId?: number;
  searchQuery?: string;
  filters?: any;
}) {
  console.log('ShipmentsTable rendered with storeId:', storeId);
  const { t } = useLanguage();
  const { token } = useAuthStore();
  
  const { fulfillmentFilters, setFulfillmentPagination } = useFilterStore();
  const page = fulfillmentFilters.page;
  const itemsPerPage = fulfillmentFilters.limit;

  // Get operation filter state for filtering functionality
  const {
    searchQuery: operationSearchQuery,
    filters: operationFilters,
    marketplaceFilter: operationMarketplaceFilter
  } = useOperationFilterStore();

  const { shipments, total, isLoading, error, fetchShipments } = useShipmentsStore();
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  
  // Sorting state
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Drawer states
  const [isCommercialInvoiceDrawerOpen, setIsCommercialInvoiceDrawerOpen] = useState(false);
  const [isAdjustDrawerOpen, setIsAdjustDrawerOpen] = useState(false);
  const [isEntryDrawerOpen, setIsEntryDrawerOpen] = useState(false);
  const [isSecondaryCurrencyDrawerOpen, setIsSecondaryCurrencyDrawerOpen] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(null);
  
  const totalPages = Math.ceil(total / itemsPerPage);

  const handlePageChange = (newPage: number) => {
    setFulfillmentPagination(newPage, itemsPerPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setFulfillmentPagination(1, newItemsPerPage);
  };

  // Handle sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default desc order
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Drawer handlers
  const handleCommercialInvoiceClick = () => {
    setIsCommercialInvoiceDrawerOpen(true);
  };

  const handleAdjustClick = (shipment: Shipment) => {
    setSelectedShipment(shipment);
    setIsAdjustDrawerOpen(true);
  };

  const handleEntryClick = (shipment: Shipment) => {
    setSelectedShipment(shipment);
    setIsEntryDrawerOpen(true);
    setOpenDropdown(null);
  };

  const handleSecondaryCurrencyClick = (shipment: Shipment) => {
    setSelectedShipment(shipment);
    setIsSecondaryCurrencyDrawerOpen(true);
    setOpenDropdown(null);
  };

  // Convert operation marketplace filter format (uk_mkt) to marketplace names
  const convertMarketplaceFilters = (filters: string[]) => {
    const mapping: Record<string, string> = {
      'uk_mkt': 'UK Market',
      'us_mkt': 'US Market', 
      'eu_mkt': 'EU Market',
      'tr_mkt': 'TR Market',
      'gf_mkt': 'Gulf Market',
    };
    return filters.map(filter => mapping[filter] || filter).filter(Boolean);
  };

  useEffect(() => {
    console.log('ShipmentsTable useEffect triggered:', { 
      token: !!token, 
      storeId, 
      page, 
      itemsPerPage,
      operationSearchQuery,
      operationMarketplaceFilter,
      operationFilters
    });
    if (!token || !storeId) {
      console.log('ShipmentsTable: Missing token or storeId, not fetching');
      return;
    }
    
    // Build filters object from operation filter store
    const filters = {
      search: operationSearchQuery || undefined,
      marketplace_filter: operationMarketplaceFilter?.length > 0 ? convertMarketplaceFilters(operationMarketplaceFilter) : undefined,
      date_from: operationFilters?.orderDateFrom || undefined,
      date_to: operationFilters?.orderDateTo || undefined,
    };

    console.log('ShipmentsTable: Calling fetchShipments with filters:', filters);
    fetchShipments({ 
      token, 
      storeId, 
      page, 
      limit: itemsPerPage, 
      filters: {
        ...filters,
        sort: sortField,
        order: sortOrder
      }
    });
  }, [storeId, page, itemsPerPage, token, fetchShipments, operationSearchQuery, operationMarketplaceFilter, operationFilters, sortField, sortOrder]);

  useEffect(() => {
    handlePageChange(1);
  }, [storeId]);

  // Reset to first page when sorting changes
  useEffect(() => {
    handlePageChange(1);
  }, [sortField, sortOrder]);

  // Listen for refresh events from operation page
  useEffect(() => {
    const handleRefreshOperationData = () => {
      console.log('🔄 Refreshing shipments data...');
      if (!token || !storeId) return;
      
      const filters = {
        search: operationSearchQuery || undefined,
        marketplace_filter: operationMarketplaceFilter?.length > 0 ? convertMarketplaceFilters(operationMarketplaceFilter) : undefined,
        date_from: operationFilters?.orderDateFrom || undefined,
        date_to: operationFilters?.orderDateTo || undefined,
      };
      
      fetchShipments({ 
      token, 
      storeId, 
      page, 
      limit: itemsPerPage, 
      filters: {
        ...filters,
        sort: sortField,
        order: sortOrder
      }
    });
    };

    window.addEventListener('refresh-operation-data', handleRefreshOperationData);
    
    return () => {
      window.removeEventListener('refresh-operation-data', handleRefreshOperationData);
    };
  }, [token, storeId, page, itemsPerPage, fetchShipments, operationSearchQuery, operationMarketplaceFilter, operationFilters]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (openDropdown !== null && !target.closest('.dropdown-container')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm mt-[-16px] border border-gray-200 mx-16 p-8">
        <div className="flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          <span className="ml-2 text-gray-600">{t('common.loading')}...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-sm mt-[-16px] border border-gray-200 mx-16 p-8">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-2">Error loading shipments</div>
          <div className="text-gray-600 text-sm mb-4">{error}</div>
          <button 
            onClick={() => {
              if (!token || !storeId) return;
              fetchShipments({ 
      token, 
      storeId, 
      page, 
      limit: itemsPerPage,
      filters: {
        sort: sortField,
        order: sortOrder
      }
    });
            }}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            {t('common.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  if (shipments.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm mt-[-16px] border border-gray-200 mx-16 p-8">
        <div className="text-center">
          <div className="text-gray-500 text-lg mb-2">No shipments found</div>
          <div className="text-gray-400 text-sm">No shipments match the current criteria</div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Total Count and Create Commercial Invoice button between filter and table */}
      <div className="flex justify-between items-center p-4 mx-16">
        <h2 className="text-lg font-semibold text-gray-900">Total Count: {total}</h2>
        <button 
          onClick={handleCommercialInvoiceClick}
          className="bg-yellow-500 text-black px-6 py-2 rounded-lg font-medium hover:bg-yellow-600 transition-colors"
        >
          Create commercial Invoice
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mx-16">
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-[#EDF1FF] border-b border-gray-200">
              <th className="py-3 px-4 text-left border-r border-gray-200">
                <CustomCheckbox />
              </th>
              <th 
                className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200 cursor-pointer hover:bg-blue-50 transition-colors"
                onClick={() => handleSort('id')}
              >
                <div className="flex items-center gap-1">
                  Sys ID
                  <ChevronsUpDown 
                    size={14} 
                    className={`${
                      sortField === 'id' 
                        ? sortOrder === 'asc' 
                          ? 'text-blue-600 rotate-180' 
                          : 'text-blue-600' 
                        : 'text-gray-400'
                    }`} 
                  />
                </div>
              </th>
              <th 
                className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200 cursor-pointer hover:bg-blue-50 transition-colors"
                onClick={() => handleSort('shipment_reference')}
              >
                <div className="flex items-center gap-1">
                  Shipment Reference
                  <ChevronsUpDown 
                    size={14} 
                    className={`${
                      sortField === 'shipment_reference' 
                        ? sortOrder === 'asc' 
                          ? 'text-blue-600 rotate-180' 
                          : 'text-blue-600' 
                        : 'text-gray-400'
                    }`} 
                  />
                </div>
              </th>
              <th 
                className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200 cursor-pointer hover:bg-blue-50 transition-colors"
                onClick={() => handleSort('created_at')}
              >
                <div className="flex items-center gap-1">
                  Shipment Created D/T
                  <ChevronsUpDown 
                    size={14} 
                    className={`${
                      sortField === 'created_at' 
                        ? sortOrder === 'asc' 
                          ? 'text-blue-600 rotate-180' 
                          : 'text-blue-600' 
                        : 'text-gray-400'
                    }`} 
                  />
                </div>
              </th>
              <th 
                className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200 cursor-pointer hover:bg-blue-50 transition-colors"
                onClick={() => handleSort('total')}
              >
                <div className="flex items-center gap-1">
                  Total
                  <ChevronsUpDown 
                    size={14} 
                    className={`${
                      sortField === 'total' 
                        ? sortOrder === 'asc' 
                          ? 'text-blue-600 rotate-180' 
                          : 'text-blue-600' 
                        : 'text-gray-400'
                    }`} 
                  />
                </div>
              </th>
              <th 
                className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200 cursor-pointer hover:bg-blue-50 transition-colors"
                onClick={() => handleSort('mkt_name')}
              >
                <div className="flex items-center gap-1">
                  Marketplace
                  <ChevronsUpDown 
                    size={14} 
                    className={`${
                      sortField === 'mkt_name' 
                        ? sortOrder === 'asc' 
                          ? 'text-blue-600 rotate-180' 
                          : 'text-blue-600' 
                        : 'text-gray-400'
                    }`} 
                  />
                </div>
              </th>
              <th 
                className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200 cursor-pointer hover:bg-blue-50 transition-colors"
                onClick={() => handleSort('master_way_bill')}
              >
                <div className="flex items-center gap-1">
                  Master Way Bill
                  <ChevronsUpDown 
                    size={14} 
                    className={`${
                      sortField === 'master_way_bill' 
                        ? sortOrder === 'asc' 
                          ? 'text-blue-600 rotate-180' 
                          : 'text-blue-600' 
                        : 'text-gray-400'
                    }`} 
                  />
                </div>
              </th>
              <th 
                className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200 cursor-pointer hover:bg-blue-50 transition-colors"
                onClick={() => handleSort('orig_tracking_no')}
              >
                <div className="flex items-center gap-1">
                  Tracking No
                  <ChevronsUpDown 
                    size={14} 
                    className={`${
                      sortField === 'orig_tracking_no' 
                        ? sortOrder === 'asc' 
                          ? 'text-blue-600 rotate-180' 
                          : 'text-blue-600' 
                        : 'text-gray-400'
                    }`} 
                  />
                </div>
              </th>
              <th 
                className="py-3 px-4 text-left text-sm font-medium text-gray-700 border-r border-gray-200 cursor-pointer hover:bg-blue-50 transition-colors"
                onClick={() => handleSort('mkt_product_name')}
              >
                <div className="flex items-center gap-1">
                  Product Name
                  <ChevronsUpDown 
                    size={14} 
                    className={`${
                      sortField === 'mkt_product_name' 
                        ? sortOrder === 'asc' 
                          ? 'text-blue-600 rotate-180' 
                          : 'text-blue-600' 
                        : 'text-gray-400'
                    }`} 
                  />
                </div>
              </th>
              <th className="py-3 px-4 text-left text-sm font-medium text-gray-700">
                <div className="flex items-center gap-1">
                  Action
                  <ChevronsUpDown size={14} className="text-gray-400" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {shipments.map((shipment) => (
              <tr key={shipment.id} className="hover:bg-gray-50">
                <td className="py-3 px-4 border-r border-gray-200">
                  <CustomCheckbox />
                </td>
                <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">
                  {shipment.id}
                </td>
                <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">
                  {/* {new Date(shipment.created_at).toLocaleDateString('en-US', { 
                    day: '2-digit', 
                    month: 'short', 
                    year: 'numeric' 
                  })} */}
                   {shipment.shipment_reference}
                </td>
                <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">
                  {new Date(shipment.created_at).toLocaleString('en-US', { 
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  })}
                  {/* {shipment.shipment_reference} */}
                </td>
                <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">
                  {shipment.total || '0.00'}
                </td>
                <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">
                  {shipment.mkt_name || 'Not Set'}
                </td>
                <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">
                  {shipment.master_way_bill || 'N/A'}
                </td>
                <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">
                  {shipment.orig_tracking_no || 'N/A'}
                </td>
                <td className="py-3 px-4 text-sm text-gray-900 border-r border-gray-200">
                  {shipment.mkt_product_name || 'N/A'}
                </td>
                <td className="py-3 px-4 border-r border-gray-200">
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleAdjustClick(shipment)}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-yellow-500 text-black text-sm font-medium hover:bg-yellow-600 transition-colors"
                    >
                      Adjust
                    </button>
                    <div className="relative dropdown-container">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDropdown(openDropdown === shipment.id ? null : shipment.id);
                        }}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-black text-white text-sm font-medium hover:bg-gray-800 transition-colors"
                      >
                        ⋮
                      </button>
                      {openDropdown === shipment.id && (
                        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[120px]">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEntryClick(shipment);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-100"
                          >
                            Entry
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSecondaryCurrencyClick(shipment);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            Secondary Currency
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700">Show</span>
          <select 
            value={itemsPerPage} 
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 text-sm text-gray-700"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
          </select>
          <span className="text-sm text-gray-700">per page</span>
        </div>
        
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-700">
            {((page - 1) * itemsPerPage) + 1}-{Math.min(page * itemsPerPage, total)} of {total}
          </span>
          
          <div className="flex items-center gap-1">
            <button 
              onClick={() => handlePageChange(Math.max(1, page - 1))}
              disabled={page === 1}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-1 text-sm rounded ${
                    page === pageNum 
                      ? 'bg-[#F1F1F4] text-gray-900 font-medium' 
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button 
              onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
      </div>
    </div>

      {/* Side Drawers */}
      <CommercialInvoiceDrawer 
        isOpen={isCommercialInvoiceDrawerOpen} 
        onClose={() => setIsCommercialInvoiceDrawerOpen(false)} 
      />
      
      <AdjustDrawer 
        isOpen={isAdjustDrawerOpen} 
        onClose={() => setIsAdjustDrawerOpen(false)} 
        shipment={selectedShipment}
      />
      
      <EntryDrawer 
        isOpen={isEntryDrawerOpen} 
        onClose={() => setIsEntryDrawerOpen(false)} 
        shipment={selectedShipment}
      />
      
      <SecondaryCurrencyDrawer 
        isOpen={isSecondaryCurrencyDrawerOpen} 
        onClose={() => setIsSecondaryCurrencyDrawerOpen(false)} 
        shipment={selectedShipment}
      />
    </>
  );
}
